import type { ChatModelId } from '@/components/Messenger/agent/agentChatSharedTypes';
import type { TrackerChatMessage } from '@/components/Messenger/types/chatMessageTypes';
import { resolveAgentPartsFileTarget } from '@/components/Messenger/shared/messages/messengerAttachmentTarget';

export function isValidAgentModelSlot(v: unknown): v is ChatModelId {
  return v === 'premium' || v === 'premium_pro';
}

function mergeAssistantPartsPreferFile(
  incomingParts: TrackerChatMessage['parts'] | undefined,
  prevParts: TrackerChatMessage['parts'] | undefined
): TrackerChatMessage['parts'] {
  const incoming = incomingParts ?? [];
  const fileTarget =
    resolveAgentPartsFileTarget(incoming) ?? resolveAgentPartsFileTarget(prevParts);
  if (!fileTarget) return incoming;

  const textParts = incoming.filter((p) => p.type === 'text');
  const filePart = incoming.find((p) => p.type === 'file' && 'url' in p) ??
    prevParts?.find((p) => p.type === 'file' && 'url' in p);
  if (!filePart) {
    return [
      {
        type: 'file' as const,
        url: fileTarget.url,
        filename: fileTarget.fileName,
        mediaType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      ...textParts,
    ] as TrackerChatMessage['parts'];
  }
  return [filePart, ...textParts] as TrackerChatMessage['parts'];
}

/**
 * Один источник правды для слияния двух снимков одного UI-сообщения ассистента из разных каналов
 * (сокет/API ↔ `useChat` / AI SDK).
 *
 * - Валидный `modelSlot` во **входящем** сообщении — канон (ответ сервера).
 * - Если во входящем слота нет, а в предыдущем уже был валидный слот — переносим его,
 *   чтобы финализация стрима SDK не затирала мету, уже пришедшую с сервера.
 */
export function coalesceAssistantMessageFromSources(
  prevMsg: TrackerChatMessage,
  incoming: TrackerChatMessage
): TrackerChatMessage {
  if (prevMsg.role !== 'assistant' || incoming.role !== 'assistant') return incoming;
  const iSlot = incoming.metadata?.modelSlot;
  const pSlot = prevMsg.metadata?.modelSlot;
  if (isValidAgentModelSlot(iSlot)) {
    return {
      ...incoming,
      parts: mergeAssistantPartsPreferFile(incoming.parts, prevMsg.parts),
    };
  }
  if (!isValidAgentModelSlot(iSlot) && isValidAgentModelSlot(pSlot)) {
    return {
      ...incoming,
      parts: mergeAssistantPartsPreferFile(incoming.parts, prevMsg.parts),
      metadata: {
        ...(incoming.metadata ?? {}),
        modelSlot: pSlot,
        messageCreatedAt:
          incoming.metadata?.messageCreatedAt ?? prevMsg.metadata?.messageCreatedAt,
      },
    };
  }
  return {
    ...incoming,
    parts: mergeAssistantPartsPreferFile(incoming.parts, prevMsg.parts),
  };
}
